import {Component, OnInit, OnDestroy} from '@angular/core';
import {Router, ActivatedRoute} from "@angular/router";
import { PaymentService } from 'src/services/payment.service';
import { WebhookService } from 'src/services/webhook.service';
import { RouteHelperService } from 'src/services/route-helper.service';

@Component({
  selector: 'app-success',
  templateUrl: './success.component.html',
  styleUrls: ['./success.component.scss']
})
export class SuccessComponent implements OnInit, OnDestroy {
  countdown: number = 15;
  transactionDetails: any = {
    checkoutId: null,
    reference: 'Processing...',
    amount: 'Processing...',
    date: new Date(),
    status: 'Loading...',
    plateNumber: null,
    zoneName: null,
    // Bulk payment support
    plateNumbers: [],
    zoneNames: [],
    sessionCount: 1,
    isBulkPayment: false,
    sessionsDetail: []
  };
  isVerifying: boolean = true;
  verificationComplete: boolean = false;
  verificationError: string = '';

  constructor(
    private router: Router, 
    private route: ActivatedRoute,
    private paymentService: PaymentService,
    private webhookService: WebhookService,
    public routeHelper: RouteHelperService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(async params => {
      
      // Maya can return parameters in different formats, try multiple ways to extract checkout ID
      const checkoutId = this.extractCheckoutId(params);
      const reference = params['reference'] || params['referenceNumber'] || params['ref'];
      const amount = params['amount'] || params['totalAmount'] || params['value'];

      this.transactionDetails = {
        checkoutId: checkoutId,
        reference: reference || 'Processing...',
        amount: amount || 'Processing...',
        date: new Date(),
        status: 'Verifying payment...'
      };

      // Always attempt to verify payment, even without obvious parameters
      if (checkoutId) {
        await this.verifyPaymentStatus(checkoutId);
      } else {
        // Try to extract checkout ID from URL path or other sources
        await this.attemptPaymentRecovery(params);
      }
    });
  }

  private extractCheckoutId(params: any): string | null {
    // Try multiple parameter names that Maya might use
    const possibleIds = [
      'checkoutId', 'id', 'checkout_id', 'transactionId', 
      'transaction_id', 'payment_id', 'session_id'
    ];
    
    for (const key of possibleIds) {
      if (params[key]) {
        return params[key];
      }
    }
    
    // Try to extract from URL fragments or other sources
    const urlParams = new URLSearchParams(window.location.search);
    for (const key of possibleIds) {
      const value = urlParams.get(key);
      if (value) {
        return value;
      }
    }
    
    // Check if there's an ID in the URL hash/fragment
    const hash = window.location.hash;
    if (hash && hash.includes('id=')) {
      const match = hash.match(/id=([^&]+)/);
      if (match && match[1]) {
        return match[1];
      }
    }
    
    return null;
  }

  private async attemptPaymentRecovery(params: any): Promise<void> {
    this.isVerifying = true;
    
    try {
      // Try to find recent payment from current user's session
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user && user.uid) {
        
        // Check for recent online payments for this user
        const recentPayment = await this.findRecentUserPayment(user.uid);
        if (recentPayment) {
          
          this.transactionDetails.checkoutId = recentPayment.checkout_id;
          this.transactionDetails.reference = recentPayment.transaction_reference || recentPayment.id;
          this.transactionDetails.amount = recentPayment.amount;
          
          // Handle bulk vs single payment
          if (recentPayment.isBulkPayment) {
            this.transactionDetails.isBulkPayment = true;
            this.transactionDetails.sessionCount = recentPayment.sessionCount;
            this.transactionDetails.plateNumbers = recentPayment.plateNumbers;
            this.transactionDetails.zoneNames = recentPayment.zoneNames;
            // For display purposes, show first plate/zone
            this.transactionDetails.plateNumber = recentPayment.plateNumbers[0] || 'Multiple';
            this.transactionDetails.zoneName = recentPayment.zoneNames[0] || 'Multiple Zones';
          } else {
            this.transactionDetails.plateNumber = recentPayment.plate_number;
            this.transactionDetails.zoneName = recentPayment.zone_name;
          }
          
          await this.verifyPaymentStatus(recentPayment.checkout_id);
          return;
        }
      }
      
      // If all else fails, show generic success message
      this.showGenericSuccess();
      
    } catch (error) {
      console.error('Payment recovery failed:', error);
      this.showGenericSuccess();
    }
  }
  
  private async findRecentUserPayment(userId: string): Promise<any> {
    try {
      // This would typically call your payment service to find recent payments
      // For now, we'll check localStorage for any stored payment info
      const storedPayment = localStorage.getItem('lastPaymentAttempt');
      if (storedPayment) {
        const paymentData = JSON.parse(storedPayment);
        // Check if it's from the last 10 minutes
        const paymentTime = new Date(paymentData.timestamp);
        const now = new Date();
        const diffMinutes = (now.getTime() - paymentTime.getTime()) / (1000 * 60);
        
        if (diffMinutes <= 10) {
          
          // Handle both bulk and single payments
          if (paymentData.session_count > 1 || paymentData.parking_session_ids) {
            paymentData.isBulkPayment = true;
            paymentData.sessionCount = paymentData.session_count || (paymentData.parking_session_ids ? paymentData.parking_session_ids.length : 1);
            paymentData.plateNumbers = paymentData.plate_numbers || [];
            paymentData.zoneNames = paymentData.zone_names || [];
          }
          
          return paymentData;
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error finding recent payment:', error);
      return null;
    }
  }
  
  private showGenericSuccess(): void {
    this.transactionDetails.status = 'Payment Completed';
    this.verificationComplete = true;
    this.isVerifying = false;
    this.verificationError = '';
    
    // Show success but indicate that details are being processed
    this.transactionDetails.reference = 'Payment processed successfully';
    this.transactionDetails.amount = 'Details being updated...';
    
    this.startCountdown();
  }

  async verifyPaymentStatus(checkoutId: string) {
    try {
      this.isVerifying = true;
      
      // First, try to get payment details from online_payments collection
      const onlinePayment = await this.paymentService.getOnlinePaymentByCheckoutId(checkoutId);
      
      if (onlinePayment) {
        
        // Update transaction details with found information
        this.transactionDetails.amount = onlinePayment.amount || this.transactionDetails.amount;
        this.transactionDetails.reference = onlinePayment.transaction_reference || onlinePayment.id;
        
        // Handle bulk vs single payment from online_payments record
        if (onlinePayment.session_count > 1 || onlinePayment.parking_session_ids) {
          this.transactionDetails.isBulkPayment = true;
          this.transactionDetails.sessionCount = onlinePayment.session_count || (onlinePayment.parking_session_ids ? onlinePayment.parking_session_ids.length : 1);
          this.transactionDetails.plateNumbers = onlinePayment.plate_numbers || [];
          this.transactionDetails.zoneNames = onlinePayment.zone_names ? onlinePayment.zone_names.split(', ') : [];
          // For display purposes, show summary
          this.transactionDetails.plateNumber = this.transactionDetails.plateNumbers.length > 1 ? 
            `${this.transactionDetails.plateNumbers.length} vehicles` : 
            (this.transactionDetails.plateNumbers[0] || onlinePayment.plate_number);
          this.transactionDetails.zoneName = this.transactionDetails.zoneNames.length > 1 ? 
            'Multiple zones' : 
            (this.transactionDetails.zoneNames[0] || onlinePayment.zone_name);
        } else {
          this.transactionDetails.plateNumber = onlinePayment.plate_number;
          this.transactionDetails.zoneName = onlinePayment.zone_name;
        }
        
        // Check if already marked as paid
        if (onlinePayment.status === 'paid' || onlinePayment.payment_status === 'paid' || 
            onlinePayment.status === 'completed' || onlinePayment.payment_status === true) {
          this.transactionDetails.status = 'Payment Successful';
          this.verificationComplete = true;
          this.startCountdown();
          return;
        }
      }
      
      // Try to verify via payment service
      try {
        const paymentResult = await this.paymentService.verifyPaymentStatus(checkoutId);
        
        if (paymentResult && paymentResult.success) {
          this.transactionDetails.status = 'Payment Successful';
          this.transactionDetails.amount = paymentResult.payment?.amount || this.transactionDetails.amount;
          this.transactionDetails.plateNumber = paymentResult.payment?.plate_number || this.transactionDetails.plateNumber;
          this.transactionDetails.zoneName = paymentResult.payment?.zone_name || this.transactionDetails.zoneName;
          this.verificationComplete = true;
        } else {
          throw new Error('Payment service verification failed');
        }
      } catch (serviceError) {
        console.log('Payment service failed, trying webhook service:', serviceError);
        
        // Fallback: try webhook service verification
        try {
          await this.webhookService.verifyAndSyncPayment(checkoutId);
          this.transactionDetails.status = 'Payment Verified';
          this.verificationComplete = true;
        } catch (webhookError) {
          console.log('Webhook service also failed:', webhookError);
          
          // Final fallback: assume success but indicate processing
          this.transactionDetails.status = 'Payment Processing';
          this.verificationComplete = true;
          this.verificationError = 'Payment completed successfully. Details are being processed and will be updated in your account shortly.';
        }
      }

      this.startCountdown();

    } catch (error) {
      console.error('Payment verification error:', error);
      
      // Don't show error for successful payments, just indicate processing
      this.transactionDetails.status = 'Payment Completed';
      this.verificationComplete = true;
      this.verificationError = 'Your payment has been processed successfully. If details don\'t appear immediately, they will be updated shortly.';
      this.startCountdown();
    } finally {
      this.isVerifying = false;
    }
  }

  private startCountdown() {
    // Clear any existing intervals
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    
    this.countdownInterval = setInterval(() => {
      this.countdown--;
      if (this.countdown === 0) {
        clearInterval(this.countdownInterval);
        this.goToDashboard();
      }
    }, 1000);
  }
  
  private countdownInterval: any;

  goToDashboard() {
    // Clear any stored payment attempt data
    localStorage.removeItem('lastPaymentAttempt');
    
    // Clear countdown interval
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    
    this.router.navigate([this.routeHelper.getRoutes().dashboard], {
      queryParams: { payment_success: 'true' }
    });
  }
  
  ngOnDestroy() {
    // Clean up interval on component destroy
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  formatDate(date: any): string {
    if (!date) return 'N/A';
    try {
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  }

  downloadReceipt() {
    // Create a simple receipt
    const receiptContent = `
      PARKBOT PH - Payment Receipt
      ============================
      Reference: ${this.transactionDetails?.reference}
      Amount: ₱${this.transactionDetails?.amount}
      Date: ${this.formatDate(this.transactionDetails?.date)}
      Status: Payment Successful
      ============================
      Thank you for using Parkbot PH!
    `;
    
    const blob = new Blob([receiptContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `parkbot-receipt-${this.transactionDetails?.reference || 'receipt'}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }
}
